import styled from "@emotion/styled";
import { forwardRef, useImperativeHandle, useState, useEffect, useRef } from "react";
import dayjs from 'dayjs';
import { i18n, LongestTripDurationEnum, TravelDestinationEnum, TravelDestinationEnumLabel, TravelInsuranceTypeEnum, TravelInsuranceTypeEnumLabel } from "src/common";
import { focusFirstInvalid } from "src/common/style/a11y.style";
import { InputDateRangePicker, InputNumber, RadioButtonGroup, validateDateSameOrAfter, validateFutureDate } from "src/components";
import { colors } from "src/theme";
import { Text } from "../typography";
import { FormRef } from "./form";

export type BasicDataFormItemType = 'insuranceType' | 'insuranceStartDate' | 'insuranceEndDate' | 'travelDestination' | 'longestTripDuration' | 'longestTripDurationDays';
export const allBasicDataFormItems: Array<BasicDataFormItemType> = ['insuranceType', 'insuranceStartDate', 'insuranceEndDate', 'travelDestination', 'longestTripDuration'];//, 'longestTripDurationDays';

export interface BasicDataFormValues {
  insuranceType: TravelInsuranceTypeEnum; 
  insuranceStartDate: string;
  insuranceEndDate?: string;
  travelDestination: TravelDestinationEnum;
  longestTripDuration: LongestTripDurationEnum;
  longestTripDurationDays: string;
}

interface BasicDataFormProps {
  basicData?: BasicDataFormValues;
  formItems?: Array<BasicDataFormItemType>;
  onSubmit?: (values: BasicDataFormValues) => void;
}

const TextStyle = styled(Text)({
  marginTop: 8
})

const FormContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px'
})

export const BasicDataForm = forwardRef<FormRef, BasicDataFormProps>(({ basicData, formItems = allBasicDataFormItems, onSubmit }, ref) => {
  const [formValues, setFormValues] = useState<BasicDataFormValues>({
    insuranceType: basicData?.insuranceType || TravelInsuranceTypeEnum.YEAR,
    insuranceStartDate: basicData?.insuranceStartDate || '',
    insuranceEndDate: basicData?.insuranceEndDate || '',
    travelDestination: basicData?.travelDestination || TravelDestinationEnum.EUROPE,
    longestTripDuration: basicData?.longestTripDuration || LongestTripDurationEnum.LESS_THAN_THREE_MONTHS,
    longestTripDurationDays: basicData?.longestTripDurationDays || '0',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const prevInsuranceTypeRef = useRef<TravelInsuranceTypeEnum>(formValues.insuranceType);

  // Clear end date when insuranceType changes
  useEffect(() => {
    if (prevInsuranceTypeRef.current !== formValues.insuranceType) {
      prevInsuranceTypeRef.current = formValues.insuranceType;
      setFormValues(prev => ({
        ...prev,
        insuranceEndDate: ''
      }));
      // Clear end date error when insurance type changes
      setErrors(prev => ({
        ...prev,
        insuranceEndDate: ''
      }));
    }
  }, [formValues.insuranceType]);

  // Auto-calculate end date when insuranceType is YEAR and start date changes
  useEffect(() => {
    if (formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && formValues.insuranceStartDate) {
      const startDate = dayjs(formValues.insuranceStartDate);
      if (startDate.isValid()) {
        const calculatedEndDate = startDate.add(364, 'days').format('YYYY-MM-DD');
        setFormValues(prev => {
          // Only update if the calculated date is different from current
          if (prev.insuranceEndDate !== calculatedEndDate) {
            return {
              ...prev,
              insuranceEndDate: calculatedEndDate
            };
          }
          return prev;
        });
      }
    } else if (formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && !formValues.insuranceStartDate) {
      // Clear end date if switching to YEAR but no start date yet
      setFormValues(prev => ({
        ...prev,
        insuranceEndDate: ''
      }));
    }
  }, [formValues.insuranceStartDate, formValues.insuranceType]);

  const handleFieldChange = (fieldName: keyof BasicDataFormValues, value: any) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleDateRangeChange = (dateRange: { startDate: string; endDate: string }) => {
    setFormValues(prev => {
      const newValues = {
        ...prev,
        insuranceStartDate: dateRange.startDate,
        insuranceEndDate: dateRange.endDate
      };

      // If YEAR mode is active, don't allow manual end date changes
      // The useEffect will automatically set it to startDate + 365 days
      if (prev.insuranceType === TravelInsuranceTypeEnum.YEAR && dateRange.startDate) {
        const startDate = dayjs(dateRange.startDate);
        if (startDate.isValid()) {
          newValues.insuranceEndDate = startDate.add(364, 'days').format('YYYY-MM-DD');
        }
      }

      return newValues;
    });
    
    // Clear errors when user starts typing
    if (errors.insuranceStartDate || errors.insuranceEndDate) {
      setErrors(prev => ({
        ...prev,
        insuranceStartDate: '',
        insuranceEndDate: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate insurance start date
    if (!formValues.insuranceStartDate) {
      newErrors.insuranceStartDate = i18n.t('components.forms.basicData.insuranceStartDateRequired');
    }

    if (formValues.insuranceStartDate && !validateFutureDate(formValues.insuranceStartDate)) {
      newErrors.insuranceStartDate = i18n.t('components.forms.basicData.insuranceStartDateFutureDate');
    }

    // Validate insurance end date
    if (!formValues.insuranceEndDate) {
      newErrors.insuranceEndDate = i18n.t('components.forms.basicData.insuranceEndDateRequired');
    }

    // For SINGLE insurance type, validate end date is same as or after start date (one-day trips allowed)
    if (formValues.insuranceType === TravelInsuranceTypeEnum.SINGLE && formValues.insuranceEndDate && !validateDateSameOrAfter(formValues.insuranceEndDate, formValues.insuranceStartDate)) {
      newErrors.insuranceEndDate = i18n.t('components.forms.basicData.insuranceEndDateBiggerThanStartDate');
    }

    // For YEAR insurance type, validate end date is exactly 365 days from start date
    if (formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && formValues.insuranceStartDate && formValues.insuranceEndDate) {
      const startDate = dayjs(formValues.insuranceStartDate);
      const endDate = dayjs(formValues.insuranceEndDate);
      const expectedEndDate = startDate.add(364, 'days');
      if (!endDate.isSame(expectedEndDate, 'day')) {
        // Auto-correct if it's not exactly 365 days
        const correctedEndDate = expectedEndDate.format('YYYY-MM-DD');
        setFormValues(prev => ({
          ...prev,
          insuranceEndDate: correctedEndDate
        }));
      }
    }

    // Validate travel destination
    if (!formValues.travelDestination) {
      newErrors.travelDestination = i18n.t('components.forms.basicData.travelDestinationRequired');
    }

    // Validate longest trip duration days
    if (formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && !formValues.longestTripDurationDays) {
      newErrors.longestTripDurationDays = i18n.t('components.forms.basicData.longestTripDurationDaysRequired');
    }

    if (formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && parseInt(formValues.longestTripDurationDays) > 366) {
      newErrors.longestTripDurationDays = i18n.t('components.forms.basicData.longestTripDurationDaysMax');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formContainerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = () => {
    if (!validateForm()) {
      focusFirstInvalid(formContainerRef.current);
      return;
    }
    if (onSubmit) {
      if (formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && parseInt(formValues.longestTripDurationDays) > 90) {
        formValues.longestTripDuration = LongestTripDurationEnum.MORE_THAN_THREE_MONTHS;
      } else {
        formValues.longestTripDuration = LongestTripDurationEnum.LESS_THAN_THREE_MONTHS;
      }
      onSubmit(formValues);
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: handleSubmit,
    getValues: () => formValues,
    setFieldValue: (fieldName: string, value: any) => {
      handleFieldChange(fieldName as keyof BasicDataFormValues, value);
    },
    validateForm,
    scrollIntoView: () => {}
  })); 

  return (
    <FormContainer ref={formContainerRef}>
      {/* Insurance Type */}
      {formItems.includes('insuranceType') && (
        <div>
          <RadioButtonGroup
            label={i18n.t('components.forms.basicData.insuranceType')}
            value={formValues.insuranceType}
            onChange={(value) => handleFieldChange('insuranceType', value)}
            options={Object.values(TravelInsuranceTypeEnum).map(type => ({ 
              label: TravelInsuranceTypeEnumLabel[type], 
              value: type 
            }))}
            horizontal={true}
          />
        </div>
      )}

      {/* Date Range */}
      {formItems.includes('insuranceStartDate') && (
        <div>
          <InputDateRangePicker
            label={i18n.t('components.forms.basicData.insuranceStartDate')}
            startLabel={i18n.t('components.forms.basicData.insuranceStartDate')}
            endLabel={i18n.t('components.forms.basicData.insuranceEndDate')}
            value={{
              startDate: formValues.insuranceStartDate,
              endDate: formValues.insuranceEndDate || ''
            }}
            onChange={handleDateRangeChange}
            startError={errors.insuranceStartDate}
            endError={errors.insuranceEndDate}
            canBeLargeThanCurrentYear={true}
            showEndDate={formItems.includes('insuranceEndDate')}
            endDateDisabled={formValues.insuranceType === TravelInsuranceTypeEnum.YEAR}
          />
        </div>
      )}

      {/* Longest Trip Duration */}
      {/*formItems.includes('longestTripDuration') && formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && (
        <div>
          <RadioButtonGroup
            label={i18n.t('components.forms.basicData.longestTripDuration')}
            value={formValues.longestTripDuration}
            onChange={(value) => handleFieldChange('longestTripDuration', value)}
            options={Object.values(LongestTripDurationEnum).map(destination => ({ 
              label: LongestTripDurationEnumLabel[destination], 
              value: destination 
            }))}
            horizontal={true}
          />
        </div>
      )*/}

      {/* Longest Trip Duration Days */}
      {formItems.includes('longestTripDurationDays') && formValues.insuranceType === TravelInsuranceTypeEnum.YEAR && (
        <div>
          <InputNumber
            label={i18n.t('components.forms.basicData.longestTripDuration')}
            value={formValues.longestTripDurationDays.toString()}
            onChange={(value) => handleFieldChange('longestTripDurationDays', value)}
            step={1}
            min={1}
            max={366}
            error={errors.longestTripDurationDays}
          />
        </div>
      )}
      {/* Travel Destination */}
      {formItems.includes('travelDestination') && (
        <div>
          <RadioButtonGroup
            label={i18n.t('components.forms.basicData.travelDestination')}
            value={formValues.travelDestination}
            onChange={(value) => handleFieldChange('travelDestination', value)}
            options={Object.values(TravelDestinationEnum).map(destination => ({ 
              label: TravelDestinationEnumLabel[destination], 
              value: destination 
            }))}
            horizontal={true}
          />
          <TextStyle type="tiny" color={colors.text2}>
            {i18n.t("components.forms.basicData.travelDestinationDescription")}
          </TextStyle>
        </div>
      )}
    </FormContainer>
  );
});